import { Pill, Text } from '@innovationdepartment/proxima-ui';
import { Fragment } from 'react';
import { adAccountName, destinationKindLabel } from '../lib/adAccounts';
import { destinationDeliveryOutcome } from '../lib/deriveAudienceStatus';
import { groupSeedsWithLookalikes } from '../lib/destinationTree';
import { formatNumber, formatSpend, formatTimestamp } from '../lib/formatters';
import type {
  AdAccountsResponse,
  AudienceDelivery,
  AudienceDestination,
  AudienceDestinationStatus
} from '../types';
import {
  DeliveryErrorLine,
  NestedRowLead,
  Table,
  TableScroll,
  TreeGlyph,
  VisuallyHidden
} from './shared.styles';

const COLUMN_COUNT = 7;

type DestinationsTableProps = {
  destinations: AudienceDestination[];
  /** The latest run's latest delivery attempt per destination id. */
  latestDeliveries?: Map<number, AudienceDelivery>;
  adAccounts: AdAccountsResponse | null;
};

const NO_DELIVERIES = new Map<number, AudienceDelivery>();

/**
 * One row per destination, each ad account's seed followed by its lookalikes and any seeds it replaced, indented.
 *
 * An account with lookalikes and no seed row heads with a muted "Seed missing" row.
 */
const DestinationsTable = ({
  destinations,
  latestDeliveries = NO_DELIVERIES,
  adAccounts
}: DestinationsTableProps) => {
  if (destinations.length === 0)
    return (
      <Text variant="body2" color="neutral60">
        No destinations.
      </Text>
    );

  return (
    <TableScroll>
      <Table>
        <thead>
          <tr>
            <th>Ad account</th>
            <th>Kind</th>
            <th>Status</th>
            <th>Meta ID</th>
            <th>Reported size</th>
            <th>Spend</th>
            <th>Last spend</th>
          </tr>
        </thead>
        <tbody>
          {groupSeedsWithLookalikes(destinations).map(group => {
            const head = group.seed ?? group.lookalikes[0];
            const seedName = adAccountName(head, adAccounts);
            return (
              <Fragment key={group.key}>
                {group.seed ? (
                  <DestinationRow
                    destination={group.seed}
                    latestDelivery={latestDeliveries.get(group.seed.id)}
                    adAccounts={adAccounts}
                  />
                ) : (
                  <tr>
                    <td>
                      <small>
                        {seedName} {head.adAccountId}
                      </small>
                    </td>
                    <td colSpan={COLUMN_COUNT - 1}>
                      <small>Seed missing</small>
                    </td>
                  </tr>
                )}
                {group.lookalikes.map(row => (
                  <DestinationRow
                    key={row.id}
                    destination={row}
                    latestDelivery={latestDeliveries.get(row.id)}
                    adAccounts={adAccounts}
                    nestedUnder={`Lookalike of ${seedName} seed`}
                  />
                ))}
                {group.replacedSeeds.map(row => (
                  <DestinationRow
                    key={row.id}
                    destination={row}
                    latestDelivery={latestDeliveries.get(row.id)}
                    adAccounts={adAccounts}
                    nestedUnder={`Replaced seed of ${seedName}`}
                  />
                ))}
              </Fragment>
            );
          })}
        </tbody>
      </Table>
    </TableScroll>
  );
};

type DestinationRowProps = {
  destination: AudienceDestination;
  latestDelivery: AudienceDelivery | undefined;
  adAccounts: AdAccountsResponse | null;
  /** Screen-reader text naming the row this one nests under; a nested row is indented behind a tree glyph. */
  nestedUnder?: string;
};

const DestinationRow = ({
  destination,
  latestDelivery,
  adAccounts,
  nestedUnder
}: DestinationRowProps) => {
  const reported = destination.metadata?.reportedSize;
  const account = (
    <>
      {adAccountName(destination, adAccounts)} <small>{destination.adAccountId}</small>
      {destination.isProximaOwned ? <small> · Proxima</small> : null}
    </>
  );
  return (
    <tr>
      <td>
        {nestedUnder ? (
          <NestedRowLead>
            <TreeGlyph aria-hidden>└</TreeGlyph>
            <span>
              <VisuallyHidden>{nestedUnder}: </VisuallyHidden>
              {account}
            </span>
          </NestedRowLead>
        ) : (
          account
        )}
      </td>
      <td>{destinationKindLabel(destination)}</td>
      <td>
        <DestinationStatus destination={destination} latestDelivery={latestDelivery} />
      </td>
      <td>
        {destination.externalId ? (
          <a
            href={metaAudienceUrl(destination.adAccountId, destination.externalId)}
            target="_blank"
            rel="noreferrer"
            title={destination.externalName ?? destination.externalId}
          >
            {shortId(destination.externalId)}
          </a>
        ) : (
          '—'
        )}
      </td>
      <td>
        {reported
          ? `${formatNumber(reported.lowerBound)}–${formatNumber(reported.upperBound)}`
          : '—'}
      </td>
      <td>{formatSpend(destination.lifetimeSpend)}</td>
      <td>{destination.latestAdSpendAt ? formatTimestamp(destination.latestAdSpendAt) : '—'}</td>
    </tr>
  );
};

/**
 * The destination's status pill, or its latest delivery's outcome when that says more.
 *
 * A failed delivery shows its error under the pill.
 */
const DestinationStatus = ({
  destination,
  latestDelivery
}: Pick<DestinationRowProps, 'destination' | 'latestDelivery'>) => {
  const outcome = destinationDeliveryOutcome(destination, latestDelivery);
  if (outcome?.kind === 'failed')
    return (
      <>
        <Pill label="Delivery failed" variant="danger" />
        {outcome.errorMessage ? (
          <DeliveryErrorLine title={outcome.errorMessage}>{outcome.errorMessage}</DeliveryErrorLine>
        ) : null}
      </>
    );
  if (outcome?.kind === 'delivering') return <Pill label="Delivering" variant="info" />;
  if (outcome?.kind === 'populating') return <Pill label="Populating" variant="warning" />;
  return (
    <Pill
      label={STATUS_LABEL[destination.status] ?? destination.status}
      variant={statusVariant(destination.status)}
    />
  );
};

const STATUS_LABEL: Record<AudienceDestinationStatus, string> = {
  pending: 'Pending',
  populating: 'Populating',
  active: 'Active',
  deleted: 'Deleted'
};

const statusVariant = (status: AudienceDestinationStatus) => {
  if (status === 'active') return 'success' as const;
  if (status === 'populating') return 'warning' as const;
  return 'default' as const;
};

/* Opens the audience in Meta Ads Manager; the account number is the id without "act_" */
const metaAudienceUrl = (adAccountId: string, externalId: string) =>
  `https://adsmanager.facebook.com/adsmanager/audiences?act=${encodeURIComponent(
    adAccountId.replace(/^act_/, '')
  )}&ids=${encodeURIComponent(externalId)}`;

const shortId = (id: string) => (id.length > 10 ? `${id.slice(0, 4)}…${id.slice(-2)}` : id);

export default DestinationsTable;
